import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import Close from "@mui/icons-material/Close";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import Replay from "@mui/icons-material/Replay";
import { Box, Button, Fade, IconButton, Modal, Paper, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import React from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import { mono } from "../styles/mainStyles";

const nextPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow:
      0 0 0 0 rgba(251, 191, 36, 0.45),
      0 10px 28px rgba(251, 191, 36, 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.65);
  }
  50% {
    transform: scale(1.03);
    box-shadow:
      0 0 0 10px rgba(251, 191, 36, 0),
      0 14px 34px rgba(251, 191, 36, 0.42),
      inset 0 1px 0 rgba(255, 255, 255, 0.75);
  }
`;

const nextShimmer = keyframes`
  0% { transform: translateX(-130%) skewX(-16deg); }
  100% { transform: translateX(230%) skewX(-16deg); }
`;

/**
 * Victory overlay when the puzzle is solved (Screen 4).
 * Shows congratulations, moves, score earned, Play Again / optional Next.
 */
export default function SlidingVictoryModal({
    open,
    onClose,
    stats,
    onPlayAgain,
    onNextPuzzle,
    nextPuzzleDisabled = false,
    onSignUp,
    signUpLabel,
    showTime = false,
}) {
    const { t } = usePagesTranslation();
    const time = stats?.time ?? "0:00";
    const moves = typeof stats?.moves === "number" ? stats.moves : 0;
    const score =
        typeof stats?.score === "number"
            ? stats.score
            : typeof stats?.coins === "number"
              ? stats.coins
              : null;
    const totalScore =
        typeof stats?.totalScore === "number"
            ? stats.totalScore
            : typeof stats?.totalCoins === "number"
              ? stats.totalCoins
              : null;
    const showScore = score !== null;
    const columnCount = (showTime ? 1 : 0) + 1 + (showScore ? 1 : 0);
    const statColumns = columnCount >= 3 ? "1fr 1fr 1fr" : columnCount === 2 ? "1fr 1fr" : "1fr";

    return (
        <Modal open={open} onClose={onClose} closeAfterTransition>
            <Fade in={open}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0,0,0,0.5)",
                        backdropFilter: "blur(8px)",
                        outline: "none",
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        aria-label={t("btn.close", { defaultValue: "Close" })}
                        sx={{
                            position: "absolute",
                            top: "max(calc(env(safe-area-inset-top, 0px) + 12px), 56px)",
                            right: "max(calc(env(safe-area-inset-right, 0px) + 12px), 16px)",
                            minWidth: 44,
                            minHeight: 44,
                            color: "common.white",
                            bgcolor: "rgba(0,0,0,0.25)",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.4)" },
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Box sx={{ px: 2, width: "100%", maxWidth: 340 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                position: "relative",
                                pt: 5,
                                pb: 3,
                                px: 3,
                                borderRadius: 3,
                                textAlign: "center",
                                color: "common.white",
                                background:
                                    "linear-gradient(160deg, #1e3a5f 0%, #2563eb 45%, #0f172a 100%)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: -44,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    bgcolor: "#fbbf24",
                                    p: 1.5,
                                    borderRadius: "50%",
                                    boxShadow: 3,
                                }}
                            >
                                <EmojiEvents sx={{ fontSize: 36, color: "#713f12" }} />
                            </Box>

                            <Typography
                                variant="h5"
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    fontWeight: 800,
                                    fontFamily: mono,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                {t("native.sliding.victory.title", {
                                    defaultValue: "Congratulations!",
                                })}
                            </Typography>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: statColumns,
                                    gap: 1.5,
                                    mb: showScore && totalScore != null ? 1.25 : 2.5,
                                }}
                            >
                                {showTime ? (
                                    <Box sx={{ bgcolor: "rgba(255,255,255,0.12)", p: 1.5, borderRadius: 2 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.7, textTransform: "uppercase" }}
                                        >
                                            {t("success-card.stat-time", { defaultValue: "Time" })}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: mono,
                                                fontSize: "1.25rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {time}
                                        </Typography>
                                    </Box>
                                ) : null}
                                <Box sx={{ bgcolor: "rgba(255,255,255,0.12)", p: 1.5, borderRadius: 2 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.7, textTransform: "uppercase" }}
                                    >
                                        {t("success-card.stat-moves", { defaultValue: "Moves" })}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: mono,
                                            fontSize: "1.25rem",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {moves}
                                    </Typography>
                                </Box>
                                {showScore ? (
                                    <Box sx={{ bgcolor: "rgba(255,255,255,0.12)", p: 1.5, borderRadius: 2 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.7, textTransform: "uppercase" }}
                                        >
                                            {t("native.sliding.victory.score", {
                                                defaultValue: "Score",
                                            })}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: mono,
                                                fontSize: "1.25rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {score > 0
                                                ? t("native.sliding.victory.scoreEarned", {
                                                      defaultValue: "+{{score}}",
                                                      score,
                                                  })
                                                : score}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Box>

                            {showScore && totalScore != null ? (
                                <Typography
                                    sx={{
                                        mb: 2.5,
                                        fontSize: "0.8125rem",
                                        opacity: 0.8,
                                        fontFamily: mono,
                                    }}
                                >
                                    {t("native.sliding.victory.totalScore", {
                                        defaultValue: "Total score: {{total}}",
                                        total: totalScore,
                                    })}
                                </Typography>
                            ) : null}

                            <Stack spacing={1.25}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={onPlayAgain}
                                    startIcon={<Replay />}
                                    sx={{
                                        py: 1.25,
                                        fontWeight: 700,
                                        bgcolor: "common.white",
                                        color: "#1e40af",
                                        "&:hover": { bgcolor: "grey.100" },
                                    }}
                                >
                                    {t("native.sliding.victory.playAgain", {
                                        defaultValue: "Play Again",
                                    })}
                                </Button>
                                {typeof onNextPuzzle === "function" ? (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        disabled={nextPuzzleDisabled}
                                        onClick={onNextPuzzle}
                                        endIcon={<ArrowForwardRounded sx={{ fontSize: 22 }} />}
                                        sx={{
                                            position: "relative",
                                            overflow: "hidden",
                                            py: 1.35,
                                            fontWeight: 800,
                                            letterSpacing: "0.06em",
                                            textTransform: "uppercase",
                                            color: "#1a1205",
                                            background:
                                                "linear-gradient(185deg, #ffe9a8 0%, #fbbf24 42%, #d97706 100%)",
                                            border: "1px solid rgba(255, 240, 180, 0.95)",
                                            boxShadow:
                                                "0 10px 28px rgba(251, 191, 36, 0.32), inset 0 1px 0 rgba(255,255,255,0.65)",
                                            animation: `${nextPulse} 1.8s ease-in-out infinite`,
                                            WebkitTapHighlightColor: "transparent",
                                            "&::after": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: "42%",
                                                height: "100%",
                                                background:
                                                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                                                animation: `${nextShimmer} 2.4s ease-in-out infinite`,
                                                pointerEvents: "none",
                                            },
                                            "&:hover": {
                                                background:
                                                    "linear-gradient(185deg, #fff1c2 0%, #fcd34d 42%, #eab308 100%)",
                                            },
                                            "&:active:not(.Mui-disabled)": {
                                                transform: "scale(0.985)",
                                            },
                                            "&.Mui-disabled": {
                                                color: "rgba(26, 18, 5, 0.45)",
                                                background:
                                                    "linear-gradient(185deg, #e8d89a 0%, #c4a84a 100%)",
                                                boxShadow: "none",
                                                animation: "none",
                                                "&::after": { animation: "none", opacity: 0 },
                                            },
                                            "@media (prefers-reduced-motion: reduce)": {
                                                animation: "none",
                                                "&::after": { animation: "none", opacity: 0 },
                                            },
                                        }}
                                    >
                                        {t("native.sliding.victory.nextPuzzle", {
                                            defaultValue: "Next",
                                        })}
                                    </Button>
                                ) : null}
                                {onSignUp ? (
                                    <Button
                                        fullWidth
                                        variant="text"
                                        size="small"
                                        onClick={onSignUp}
                                        sx={{
                                            color: "rgba(255,255,255,0.75)",
                                            minHeight: 44,
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {signUpLabel ||
                                            t("native.sliding.victory.signUpCta", {
                                                defaultValue: "Sign up to save progress",
                                            })}
                                    </Button>
                                ) : null}
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
}
